import { Settings } from "lucide-react";
import { useState } from "react";

import {
  isRecordingActiveStep,
  useMicrophoneDeviceId,
  useStep,
} from "@elation/transcribe";

import { HeaderText } from "@src/shared/components/general/HeaderText";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/shared/components/ui/popover";
import { cn } from "@src/shared/lib/utils";

import { VisitLanguageSelect } from "../VisitLanguageSelect";
import { VisitMicrophoneField } from "../VisitMicrophoneSelect";

export interface VisitScribeSettingsProps {
  className?: string;
}

export function VisitScribeSettings({ className }: VisitScribeSettingsProps) {
  const [open, setOpen] = useState(false);
  const [microphoneDeviceId] = useMicrophoneDeviceId();
  const [step] = useStep();
  const languageDisabled = isRecordingActiveStep(step) || step === "finishing";
  const issueCount = microphoneDeviceId ? 0 : 1;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={
            issueCount > 0
              ? `Scribe settings, ${issueCount} issue needs attention`
              : "Scribe settings"
          }
          className={cn("relative size-8", className)}
        >
          <Settings className="size-4" aria-hidden="true" />
          {issueCount > 0 ? (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-semibold leading-none text-destructive-foreground"
            >
              {issueCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className="w-[min(calc(100vw-2rem),22rem)]"
        aria-labelledby="visit-scribe-settings-heading"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {open ? (
          <VerticalFlexbox gap="sm">
            <HeaderText
              tag="h3"
              fontWeight={600}
              className="text-sm"
              id="visit-scribe-settings-heading"
            >
              Audio settings
            </HeaderText>
            <VerticalFlexbox gap="xs">
              <HeaderText tag="h4" fontWeight={600} className="text-sm">
                Transcription settings
              </HeaderText>
              <VisitLanguageSelect disabled={languageDisabled} />
            </VerticalFlexbox>
            <VerticalFlexbox gap="xs">
              <HeaderText tag="h4" fontWeight={600} className="text-sm">
                Audio settings
              </HeaderText>
              <VisitMicrophoneField />
            </VerticalFlexbox>
          </VerticalFlexbox>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
